import Link from "next/link";
import Image from "next/image";
import type { BaseListItem } from "@/types";
import { GameHoverCard } from "./GameHoverCard";

type RankItem = BaseListItem & { rank: number; updated_ts?: number | null };
type FollowersItem = BaseListItem & {
  followers: number;
  updated_ts?: string | null;
};
type CCUItem = BaseListItem & {
  rank: number;
  online: number;
  updated_ts?: number | null;
};
type DefaultTopRankListItem = RankItem | FollowersItem | CCUItem;

interface TopRankListProps<
  TItem extends { name: string } = DefaultTopRankListItem
> {
  items: TItem[];
  primaryColor: string;
  title: string;
  linkHref: string;
  className?: string;
  headerRightLabel?: string;
  // Optional customization for different item types (e.g., publishers)
  getItemId?: (item: TItem) => number | string;
  getItemImage?: (item: TItem) => string | null;
  getItemHref?: (item: TItem, id: number | string) => string;
}

function getDefaultValue(item: {
  rank?: number;
  followers?: number;
  online?: number;
  inbound?: number;
}): string {
  if (typeof item.followers === "number")
    return item.followers.toLocaleString("en-US");
  if (typeof item.online === "number")
    return item.online.toLocaleString("en-US");
  if (typeof item.inbound === "number")
    return item.inbound.toLocaleString("en-US");
  if (typeof item.rank === "number") return `#${item.rank}`;
  return "";
}

function getOptionalStringProp(item: unknown, prop: string): string | null {
  if (typeof item !== "object" || item === null || !(prop in item)) return null;
  const v = (item as Record<string, unknown>)[prop];
  return typeof v === "string" ? v : null;
}

function getOptionalNumberProp(item: unknown, prop: string): number | null {
  if (typeof item !== "object" || item === null || !(prop in item)) return null;
  const v = (item as Record<string, unknown>)[prop];
  return typeof v === "number" ? v : null;
}

export function TopRankList<
  TItem extends { name: string } = DefaultTopRankListItem
>({
  items,
  primaryColor,
  title,
  linkHref,
  className,
  headerRightLabel,
  getItemId,
  getItemImage,
  getItemHref,
}: TopRankListProps<TItem>) {
  return (
    // `@container` on the LIST ITSELF, not on an ancestor: on the home deck a
    // "Top 5" column is one grid track, which is ~170px on a phone, and the
    // 80px capsule plus a 60px value column left ~20px for the name. Scoping
    // the queries here means the same rule works on the rank-list pages (where
    // the list is always wide, so nothing changes) and inside a deck cell.
    <div
      className={`@container bg-[#16202d] rounded-lg border border-[#2a475e] overflow-hidden shadow-lg hover:shadow-xl transition-shadow h-full flex flex-col ${
        className || ""
      }`}
    >
      {/* Header */}
      <Link
        href={linkHref}
        className={[
          "relative block border-b border-[#2a475e] cursor-pointer group",
          "bg-[#1b2838] hover:bg-[#1f2e3f] transition-colors",
        ].join(" ")}
      >
        <div className="relative px-2 py-1 sm:px-2.5 sm:py-1.5">
          <div className="flex items-center justify-between gap-2">
            <h2
              className={[
                "text-white flex items-center gap-1.5 group-hover:text-opacity-90 transition-all",
                "text-[14px] font-semibold",
              ].join(" ")}
            >
              <span className="tracking-wide">{title}</span>
            </h2>

            {headerRightLabel ? (
              <div className="text-[11px] font-semibold text-white/60 tracking-wide uppercase">
                {headerRightLabel}
              </div>
            ) : null}
          </div>
        </div>
      </Link>

      {/* Items List. `wd-rows` is the home deck's row-clipping hook (a size
          that shows fewer rows hides `.wd-rows > *:nth-child(n+K+1)`). */}
      <div className="wd-rows divide-y divide-[#2a475e] flex-1 flex flex-col">
        {items.map((item, index) => {
          const itemName = item.name;
          const appid = getOptionalNumberProp(item, "appid");
          const fallbackId = typeof appid === "number" ? appid : itemName;
          const itemId = getItemId ? getItemId(item) : fallbackId;
          const itemImage = getItemImage
            ? getItemImage(item)
            : getOptionalStringProp(item, "capsule_image");
          const itemHref = getItemHref
            ? getItemHref(item, itemId)
            : typeof appid === "number"
            ? `/app/${appid}`
            : "#";
          // Under ~260px the art is what has to go: a name nobody can read is
          // worse than a row without a picture.
          const imageSize = getItemImage
            ? "hidden @[260px]:block w-7 h-7 sm:w-9 sm:h-9"
            : "hidden @[260px]:block w-[80px] h-[30px] sm:w-[100px] sm:h-[37px]";
          const imageSizes = getItemImage ? "36px" : "100px";
          const value = getDefaultValue({
            rank: getOptionalNumberProp(item, "rank") ?? undefined,
            followers: getOptionalNumberProp(item, "followers") ?? undefined,
            online: getOptionalNumberProp(item, "online") ?? undefined,
            inbound: getOptionalNumberProp(item, "inbound") ?? undefined,
          });
          const valueIsMetric =
            typeof getOptionalNumberProp(item, "online") === "number" ||
            typeof getOptionalNumberProp(item, "followers") === "number" ||
            typeof getOptionalNumberProp(item, "inbound") === "number";
          const valueIsOnline =
            typeof getOptionalNumberProp(item, "online") === "number";

          const isGameItem = typeof appid === "number" && !getItemHref;

          const linkEl = (
            <Link
              key={itemId}
              href={itemHref}
              prefetch={false}
              className={[
                "group flex items-center relative overflow-hidden flex-1 min-h-[30px] sm:min-h-[37px]",
                `gap-1.5 sm:gap-2.5 pr-2 sm:pr-2.5 pl-0 py-0 hover:bg-[#1f2e3f] transition-colors ${index % 2 === 1 ? "bg-[#15212f]" : ""}`,
              ].join(" ")}
            >
              {/* Gradient overlay on hover */}
              <div
                className={[
                  "absolute inset-0 opacity-0 group-hover:opacity-5 transition-opacity",
                  "pointer-events-none",
                ].join(" ")}
                style={{
                  background: `linear-gradient(90deg, ${primaryColor} 0%, transparent 100%)`,
                }}
              />

              {/* Image (Game or Avatar) */}
              <div
                className={`flex-shrink-0 ${imageSize} relative overflow-hidden group-hover:border-opacity-60 transition-all`}
              >
                {itemImage ? (
                  <Image
                    src={itemImage}
                    alt={item.name}
                    fill
                    className="object-cover group-hover:scale-105 transition-transform duration-200"
                    sizes={imageSizes}
                  />
                ) : (
                  <div className="w-full h-full bg-[#2a475e] flex items-center justify-center text-[#8f98a0] text-xs">
                    {getItemImage ? "No image" : "No image"}
                  </div>
                )}
              </div>

              {/* Item Info */}
              <div className="flex-1 min-w-0">
                <h3
                  className={[
                    "truncate group-hover:underline transition-all",
                    "text-[13px] sm:text-[14px] leading-tight font-medium text-white",
                  ].join(" ")}
                >
                  {item.name}
                </h3>
              </div>

              {/* Value */}
              <div className="flex-shrink-0 tabular-nums text-right min-w-0 @[260px]:min-w-[60px] @[400px]:min-w-[80px]">
                <span
                  className={
                    valueIsOnline
                      ? "text-[#A4D007] text-[13px] sm:text-[14px]"
                      : valueIsMetric
                      ? "text-[13px] sm:text-[14px]"
                      : "text-white/90 text-[12px] sm:text-[13px]"
                  }
                  style={
                    valueIsOnline
                      ? undefined
                      : valueIsMetric
                      ? { color: primaryColor }
                      : undefined
                  }
                >
                  {value}
                </span>
              </div>
            </Link>
          );

          if (isGameItem) {
            return (
              <GameHoverCard key={itemId} appid={appid}>
                {linkEl}
              </GameHoverCard>
            );
          }

          return linkEl;
        })}
      </div>
    </div>
  );
}
